<template>
	<div :class="classes" :data-rte-stream-instance="subDocId">
		<div
			class="rte-stream-instance__container"
			ref="textEditorContainer"
			:data-creator="creator"
		>
			<div class="rte-stream-instance__title">
				<!-- TODO: possibly compute some of this in the store -->
				<TitleHeader
					class="rte-stream-instance__title-header"
					:can-add-assistant="!isAssistantEnabled && !isTranscriptionFinished"
					:can-transcribe="isTranscriptionAllowed && !isTranscriptionInProgress && !isTranscriptionFinished"
					:can-edit="canEdit"
					:can-recur="$config().enableRecurrence && canRecur && !hasRecurrence && isTranscriptionFinished"
					:document-name="documentName"
					:sub-doc-id="subDocId"
					:title="transitoryTitle || title"
					:date="date"
					:virtual-meeting-url="virtualMeetingUrl"
					@titleMenuActionSelected="onTitleMenuActionSelected"
					@titleChange="onTitleChange"
					@addAssistant="$emit( `addAssistant`, { ...$event, subDocId, } )"
					v-on="bubble(
						`assistMeeting`,
						`scheduleRecurrence`,
						`transcribe`
					)"
				/>
				<!-- FEATUREFLAG -->
				<div v-if="$config().enableTranscriptionBot && !isTranscriptionFinished"
					:key="transcriptionUiKey"
				>
					<TranscriptionBotWizard
						v-if="showBotWizard"
						v-on="bubble( `cancelJoin` )"
						:sub-doc-id="subDocId"
						:virtual-meeting-url="virtualMeetingUrl"
						:transcription-origin="transcriptionOrigin"
						:transcription-status="transcriptionStatus"
						:transcription-sub-status="transcriptionSubStatus"
						:has-status-update="transcriptionStatusCycle > 1"
						@join="$emit( `joinMeeting`, { ...$event, subDocId, } )"
						@cancelJoin="$emit( `cancelJoin`, { ...$event, subDocId, } )"
						@save="onAssignVirtualMeetingUrl"
						@hide="$emit( `hideAssistant`, { ...$event, subDocId, } )"
					/>
					<TranscriptionBotAlert
						v-if="showBotAlert"
						v-on="bubble( `cancelJoin`, `forceJoin` )"
						:sub-doc-id="subDocId"
						:virtual-meeting-url="virtualMeetingUrl"
						:transcription-origin="transcriptionOrigin"
						:transcription-status="transcriptionStatus"
						:transcription-sub-status="transcriptionSubStatus"
						:has-status-update="transcriptionStatusCycle > 1"
					/>
					<TranscriptionUploadAlert
						v-if="showUploadAlert"
						:sub-doc-id="subDocId"
						:transcription-origin="transcriptionOrigin"
						:transcription-status="transcriptionStatus"
						:transcription-sub-status="transcriptionSubStatus"
						:has-status-update="transcriptionStatusCycle > 1"
					/>
				</div>
				<div class="rte-stream-instance__title-line"></div>
			</div>

			<TranscriptionSummary
				v-if="isTranscriptionFinished && transcriptionSummary"
				:sub-doc-id="subDocId"
				:summary="transcriptionSummary"
				:itemized-summary="transcriptionItemizedSummary"
				:rating="transcriptionRating"
				v-on="bubble(
					`collapseDetails`,
					`expandDetails`,
					`rateTranscription`,
					`recommendActionItem`,
					`viewTranscription`,
				)"
			/>
			<EditorContent class="editor-content" :editor="editor"/>
		</div>
		<footer class="footer" v-if="$slots.footer">
			<slot name="footer" :is-empty="isEmpty"></slot>
		</footer>
	</div>
</template>

<script>
import debounce from "lodash/debounce";
import { EditorContent } from "@tiptap/vue-3";
import { mapGetters, mapMutations } from "vuex";

import {
	RecallAIBotStatuses as BotStatus,
	StreamDocumentType,
	TranscriptionJobStatus,
	TranscriptionOrigin,
	VirtualMeetingType
} from "@teaming/common-enums";

import {
	EventsMixin,
	TranslateMixin
} from "@teaming/common-ui";

import TitleHeader from "./TitleHeader.vue";
import TranscriptionSummary from "./TranscriptionSummary.vue";
import TranscriptionBotAlert from "./TranscriptionBotAlert.vue";
import TranscriptionUploadAlert from "./TranscriptionUploadAlert.vue";
import TranscriptionBotWizard from "./TranscriptionBotWizard.vue";

export default {
	name: "RteStreamInstance",
	teamingPackage: "common-team",
	mixins: [ EventsMixin, TranslateMixin, ],
	components: {
		EditorContent,
		TitleHeader,
		TranscriptionSummary,
		TranscriptionBotAlert,
		TranscriptionUploadAlert,
		TranscriptionBotWizard,
	},
	emits: [
		"addAssistant",
		"assignVirtualMeetingURL",
		"assistMeeting",
		"cancelJoin",
		"collapseDetails",
		"copyLink",
		"created",
		"deleteNote",
		"expandDetails",
		"forceJoin",
		"hideAssistant",
		"joinMeeting",
		"positionChanged",
		"recommendActionItem",
		"saveAsTemplate",
		"scheduleRecurrence",
		"teamingExtensionChange",
		"titleChange",
		"transcribe",
		"unassistMeeting",
		"viewTranscription",
		"checkZoomOauth",
	],
	provide() {
		return {
			context: this.context,
		};
	},
	data() {
		return {
			editor: null,
			isEmpty: true,
			transitoryTitle: "",
		};
	},
	props: {
		autofocus: {
			type: [ Boolean, Number, String, ],
			required: true,
		},
		canRecur: {
			type: Boolean,
			required: true,
		},
		context: {
			type: Object,
			default: () => ( {} ),
		},
		creator: {
			type: String,
			required: true,
		},
		recurrenceId: {
			type: String,
			required: false,
		},
		recurrenceEntryId: {
			type: String,
			required: false,
		},
		managedBy: {
			type: String,
			required: false,
			default () {
				return "ui";
			},
		},
		date: {
			type: Object, // moment.js object
			default: () => ( {} ),
		},
		docType: {
			type: String,
			required: true,
			validator: value => StreamDocumentType.includes( value ),
		},
		documentName: {
			type: String,
			required: true,
		},
		hasRecurrence: {
			type: Boolean,
			required: true,
		},
		isAssistantEnabled: {
			type: Boolean,
			required: true,
		},
		isIdentified: {
			type: Boolean,
			required: true,
		},
		meetingTemplateId: {
			type: String,
			required: false,
		},
		subDocId: {
			type: String,
			required: true,
		},
		title: {
			type: String,
			required: true,
		},
		transcriptionItemizedSummary: {
			type: Array,
			required: true,
		},
		transcriptionOrigin: {
			type: String,
			required: true,
			validator: value => TranscriptionOrigin.includes( value ) || value === "",
		},
		transcriptionRating: {
			type: Number,
			required: true,
		},
		transcriptionStatus: {
			type: String,
			required: true,
			validator: value => TranscriptionJobStatus.includes( value ) || value === "",
		},
		transcriptionStatusCycle: {
			type: Number,
			required: true,
		},
		transcriptionSubStatus: {
			type: String,
			required: true,
			validator: value => BotStatus.includes( value ) || value === "",
		},
		transcriptionSummary: {
			type: String,
			required: true,
		},
		virtualMeetingUrl: {
			type: String,
			required: true,
		},
	},
	computed: {
		...mapGetters( "TeamlikeStream", [
			"customTemplates",
			"suggestedTemplates",
		] ),
		classes() {
			return {
				"rte-stream-instance": true,
				"rte-stream-instance--pulsed": this.isIdentified,
			};
		},
		transcriptionUiKey() {
			const {
				subDocId,
				transcriptionStatus: status,
				transcriptionSubStatus: subStatus,
				virtualMeetingUrl,
			} = this;

			return `${ subDocId }:${ status }:${ subStatus }:${ virtualMeetingUrl }`;
		},
		isTranscriptionAllowed() {
			const { docType, } = this;
			return docType !== StreamDocumentType.PRIVATE;
		},
		canEdit() {
			// a user can edit if recurrence's "managed by" is not "ics". Only other option is "ui"
			const { managedBy, } = this;
			return managedBy != "ics";
		},
		isTranscriptionInProgress() {
			const { transcriptionStatus, } = this;
			return transcriptionStatus && ![
				TranscriptionJobStatus.FAILED,
				TranscriptionJobStatus.ABORTED,
				TranscriptionJobStatus.FINISHED,
			].includes( this.transcriptionStatus );
		},
		isTranscriptionFinished() {
			return this.transcriptionStatus === TranscriptionJobStatus.FINISHED;
		},
		isTranscriptionFailed() {
			return [
				TranscriptionJobStatus.FAILED,
				TranscriptionJobStatus.ABORTED,
			].includes( this.transcriptionStatus );
		},
		showUploadAlert() {
			const {
				transcriptionStatus,
				transcriptionOrigin,
			} = this;

			return transcriptionStatus &&
				transcriptionOrigin === TranscriptionOrigin.UPLOAD;
		},
		showBotAlert() {
			const {
				transcriptionStatus,
				transcriptionOrigin,
				isTranscriptionFailed,
			} = this;

			return transcriptionStatus &&
				!isTranscriptionFailed &&
				transcriptionOrigin === TranscriptionOrigin.BOT;
		},
		showBotWizard() {
			const {
				transcriptionStatus,
				transcriptionOrigin,
				isTranscriptionFailed,
				isAssistantEnabled,
			} = this;

			return ( !transcriptionStatus || isTranscriptionFailed ) &&
				transcriptionOrigin === TranscriptionOrigin.BOT &&
				isAssistantEnabled;
		},
	},
	methods: {
		...mapMutations( "TeamlikeStream", [ "showTranscriptionWizard", ] ),
		async onTitleChange( { title, } ) {
			const { subDocId, } = this;
			this.transitoryTitle = title;
			await this.$nextTick();
			const manager = this.$documentRegistry()
				.manage( this.documentName );
			manager.changeSubDocTitle( subDocId, title );
		},
		async onAssignVirtualMeetingUrl( { virtualMeetingType, virtualMeetingUrl, } ) {
			const { subDocId, } = this;
			const manager = this.$documentRegistry()
				.manage( this.documentName );
			manager.changeVirtualMeetingUrl( subDocId, virtualMeetingUrl );
			await this.$nextTick();
			this.$emit( "unassistMeeting", { subDocId, } );
			if ( virtualMeetingType === VirtualMeetingType.ZOOM ) {
				this.$emit( "checkZoomOauth" );
			}
		},
		// TODO: DELETEME
		// async onDeleteVirtualMeetingUrl() {
		// 	const { subDocId, } = this;
		// 	const manager = this.$documentRegistry()
		// 		.manage( this.documentName );
		// 	manager.changeVirtualMeetingUrl( subDocId, "" );
		// 	await this.$nextTick();
		// 	this.$emit( "unassistMeeting", { subDocId, } );
		// },
		onTeamingExtensionCallback( data ) {
			const { subDocId, } = this;
			this.$emit( "teamingExtensionChange", { ...data, subDocId, } );
		},
		onTitleMenuActionSelected( { action, } ) {
			const { subDocId, documentName, date, } = this;
			this.$emit( action, {
				subDocId,
				documentName,
				date,
			} );
		},
		setupEditor() {
			// NOTE: this will be run AFTER the provider has been
			// connected and sync'd with the actual ydoc. The editor
			// will receive it's content FROM that ydoc instance
			// (specifically the xml fragment associated with the
			// subDocId).

			if ( this.editor ) {
				return;
			}

			const { documentName, subDocId, autofocus, } = this;

			const manager = this.$documentRegistry()
				.manage( documentName );

			const placeholder = this.docType === "shared" ?
				this.translate( "sharedNotePlaceholder" ) :
				this.translate( "privateNotePlaceholder" );

			this.editor = manager.addEditor( {
				subDocId,
				placeholder,
				autofocus,
				currentUser: {
					personId: this.context.personId,
					name: this.context.name,
					color: this.context.color,
				},
				callback: this.debouncedOnTeamingExtensionCallback,
				events: {
					// NOTE: this event is used by the StreamHOC
					// to apply queued up content for the editor
					// after it has been created. This could probably
					// be done better.
					onCreate: ( { editor, } ) => {
						this.$emit( "created", { subDocId, editor, } );
					},
					// NOTE: subscribing to the actual blur/focus events
					// can be VERY tricky, so instead we are letting
					// the document registry emit events on
					// behalf of the editor, so subscribing components
					// can react accordingly
					onBlur: async () => {
						manager.blurEditor( subDocId );
					},
					// eslint-disable-next-line no-unused-vars
					onFocus: async ( { editor, transaction, } ) => {
						manager.activateEditor( subDocId );
						manager.focusEditor( subDocId );
					},
					// eslint-disable-next-line no-unused-vars
					onTransaction: async ( { transaction, } ) => {
						await this.$nextTick();
						this.isEmpty = manager.isEmpty( subDocId );
						// TODO: future implementation
						// @see StreamHOC::onPositionChanged
						// const newAnchor = transaction.selection.anchor;
						// const { lastAnchor, } = this;
						// if ( newAnchor !== lastAnchor ) {
						// 	this.lastAnchor = newAnchor;
						// 	this.$emit( "positionChanged", {
						// 		anchor: newAnchor,
						// 		subDocId,
						// 	} );
						// }
					},
					// NOTE: determine if the fragment content is empty, so this
					// can be passed up to the StreamHOC, where
					// a decision will be made on whether to show the
					// recommended templates or not
					onUpdate: async () => {
						await this.$nextTick();
						this.isEmpty = manager.isEmpty( subDocId );
					},
				},
			} );

			this.isEmpty = manager.isEmpty( subDocId );
			// FIXME: this is using the already contextualized templates from the store
			// which use the current user's name, but really should be the user that created the recurrence
			if ( this.isEmpty && this.meetingTemplateId ) {
				const selectedTemplate = [ ...this.suggestedTemplates, ...this.customTemplates, ]
					.find( template => template.meetingTemplateId === this.meetingTemplateId );
				this.editor.commands.setContent( selectedTemplate.content );
			}
		},
	},
	created() {
		this.debouncedOnTeamingExtensionCallback = debounce( data => {
			this.onTeamingExtensionCallback( data );
			// eslint-disable-next-line no-magic-numbers
		}, 4000, { maxWait: 10000, leading: true, trailing: true, } );
	},
	mounted() {
		this.setupEditor();

		if ( this.isTranscriptionFailed || this.transcriptionStatus || this.virtualMeetingUrl ) {
			this.showTranscriptionWizard( { subDocId: this.subDocId, } );
		}
	},
	updated() {
		this.setupEditor();
	},
	beforeUnmount() {
		// No need to destroy the editor here, as it
		// will be cleaned up by RteStreamList during
		// teardown.
	},
};
</script>

<style lang="scss">
.rte-stream-instance__container {
	margin-bottom: 1rem;

	.ProseMirror {
		padding: 0;
	}

	.teaming-tiptap-topic--left {
		margin-left: .75rem;
	}

	.teaming-tiptap-commitment__checkbox {
		margin-left: .75rem;
	}

	.teaming-tiptap-decision__row {
		margin-left: .75rem;
	}

	.rte-node__drag-handle {
		left: 0.15rem;
	}
}
</style>

<style lang="scss" scoped>
$rte-stream-instance-end-cap-line-color: $color1-200;
$rte-stream-instance-end-cap-text-color: $color1-700;
$rte-stream-instance-bg-color: $white;
$rte-stream-instance-box-shadow--pulsed: $color3-200;
$rte-stream-instance-bg-color--pulsed: $color5-050;
$botIconColor: $color2-500;

@keyframes pulse {
	10% {
		box-shadow: 0rem 0rem 0rem 0.1875rem $rte-stream-instance-box-shadow--pulsed;
		background-color: $rte-stream-instance-bg-color--pulsed;
	}
	25% {
		box-shadow: none;
		background-color: $rte-stream-instance-bg-color;
	}
	50% {
		box-shadow: 0rem 0rem 0rem 0.1875rem $rte-stream-instance-box-shadow--pulsed;
		background-color: $rte-stream-instance-bg-color--pulsed;
	}
	75% {
		box-shadow: none;
		background-color: $rte-stream-instance-bg-color;
	}
}

.rte-stream-instance {
	background: $rte-stream-instance-bg-color;
	padding: 1rem;
	border-radius: .5rem;
	box-shadow: 0 0.1875rem 0.375rem 0.0625rem rgba(33, 45, 99, 0.1),
	0 0.375rem 0.1875rem -1.25rem rgba(33, 45, 99, 0.2);
}

.rte-stream-instance--pulsed {
	animation-name: pulse;
	animation-duration: 2s;
}

.rte-stream-instance__title {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 0.5rem;

	width: 100%;
}

.rte-stream-instance__title-line {
	height: 0.0625rem;
	background-color: $rte-stream-instance-end-cap-line-color;
	border: none;
	margin: 0.5rem 0;
}

.rte-stream-instance .editor-content {
	margin-top: 1.5rem;
}

.rte-stream-instance .bot-icon {
	color: $botIconColor;
}
</style>
